/**
 * Organization Page
 * Main page for managing organization structure: Areas, Positions, and Cost Centers
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from '@/components/layout/PageLayout';
import { TabList } from '@/components/ui/navigation/TabList';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { SECTIONS } from '@/types/sections';
import AreasTab from './AreasTab';
import CostCentersTab from './CostCentersTab';
import PositionsTab from './PositionsTab';


type OrganizationTab = 'areas' | 'positions' | 'costCenters';

export const Organization: React.FC = () => {
  const { t: translate } = useTranslation();
  const { canSection } = usePagePermissions();
  const [activeTab, setActiveTab] = useState<OrganizationTab>('areas');
  const canViewAreas = canSection(SECTIONS.AREAS, 'view');
  const canViewPositions = canSection(SECTIONS.CARGOS, 'view');
  const canViewCostCenters = canSection(SECTIONS.CENTROS_COSTO, 'view');

  const tabs = useMemo(() => {
    const nextTabs = [
      ...(canViewAreas
        ? [{
          id: 'areas',
          label: translate('organization:tabs.areas'),
          content: <AreasTab />,
        }]
        : []),
      ...(canViewPositions
        ? [{
          id: 'positions',
          label: translate('organization:tabs.positions'),
          content: <PositionsTab />,
        }]
        : []),
      ...(canViewCostCenters
        ? [{
          id: 'costCenters',
          label: translate('organization:tabs.costCenters'),
          content: <CostCentersTab />,
        }]
        : []),
    ];

    return nextTabs;
  }, [canViewAreas, canViewPositions, canViewCostCenters, translate]);

  const defaultActiveTab = useMemo<OrganizationTab>(() => {
    if (tabs.some((tab) => tab.id === activeTab)) {
      return activeTab;
    }

    return (tabs[0]?.id as OrganizationTab | undefined) ?? 'areas';
  }, [activeTab, tabs]);

  return (
    <PageLayout>
      <div data-testid="organization-page">
        <TabList
          tabs={tabs}
          defaultActiveTab={defaultActiveTab}
          onTabChange={(id) => setActiveTab(id as OrganizationTab)}
        />
      </div>
    </PageLayout>
  );
};

export default Organization;
